import {
  RootState,
  FormState,
  VersionedStringFormState,
} from "@/lib/redux/types";
import { amountSelector } from "@/lib/misc/amountSelector";
import {
  SuccessfulKickboxAppraisal,
  PaymentSourceKind,
  IBANValidationResult,
} from "@evergiving/common/dist/es2015/pledge";
import { mapValues } from "@evergiving/common/dist/es2015/typescriptUtils";
import {
  FormConfig,
  EmailAddressFieldConfig,
  IBANFieldConfig,
  ValidatedFormFieldConfig,
  ValidationDefinition,
  RegExpValidationDefinition,
  MinimumAgeValidationDefinition,
  SoonExpirationValidationDefinition,
  ValidationKind,
  FreeFormAddressElementType,
} from "@evergiving/common/dist/es2015/form";
import {
  checkModulus,
  isCheckable,
} from "@evergiving/common/dist/es2015/ukBankAccount";
import validLuhn from "fast-luhn";
import pick from "lodash/pick";
import { Text } from "@/lib/components/Text";
import { FormattedNumber } from "@/lib/components/FormattedNumber";
import { ReactNode } from "react";
import { isValidIBAN as validIBAN } from "ibantools";

export const validationContextSelector = (state: RootState) =>
  pick(state, [
    "cardNumber",
    "directDebitAccountNumber",
    "directDebitSortCode",
  ]);

type ValidationContext = ReturnType<typeof validationContextSelector>;

function argsForValidation(validation: ValidationDefinition) {
  // FIXME: hard-wired locale. Should be fine since all numbers involved should
  // be small integers, which should render the same in pretty much any locale
  // we support so far.
  const locale = "en-US";

  if (validation.kind === ValidationKind.SoonExpiration) {
    const minimumMonths = (
      <FormattedNumber
        locale={locale}
        value={validation.minimumExpirationInMonths}
      />
    );
    return {
      minimum_months: minimumMonths,
      MINIMUM_MONTHS: minimumMonths,
    };
  }

  if (validation.kind === ValidationKind.MinimumAge) {
    const requiredAge = (
      <FormattedNumber locale={locale} value={validation.minimumAgeInYears} />
    );
    return {
      required_age: requiredAge,
      REQUIRED_AGE: requiredAge,
    };
  }
  return {};
}

export function getStringFieldValidators(
  formConfig: FormConfig,
  currentCountryCode: string | undefined,
  paymentSourceKind: PaymentSourceKind | undefined
): {
  [key in keyof VersionedStringFormState]: ValidatedFormFieldConfig | undefined;
} {
  const addressFieldConfig =
    (currentCountryCode
      ? formConfig.address?.freeForm.byCountry[currentCountryCode]
      : undefined) ?? formConfig.address?.freeForm.defaultConfig;

  return {
    customAmountString: undefined, // FIXME: should already be validated?
    firstName: formConfig.firstName,
    lastName: formConfig.lastName,
    phoneNumber: formConfig.phoneNumber,
    emailAddress: formConfig.emailAddress,
    countryCode: formConfig.country,
    dateOfBirth: formConfig.dateOfBirth,
    gender: formConfig.gender,
    belfioreCode:
      formConfig.locale === "it-IT"
        ? formConfig.personalIdentificationNumber
        : undefined,
    profession: formConfig.profession,
    title: formConfig.title,
    [FreeFormAddressElementType.Element3]: addressFieldConfig?.element3,
    [FreeFormAddressElementType.Element4]: addressFieldConfig?.element4,
    [FreeFormAddressElementType.Element5]: addressFieldConfig?.element5,
    [FreeFormAddressElementType.Element6]: addressFieldConfig?.element6,
    [FreeFormAddressElementType.Element7]: addressFieldConfig?.element7,
    [FreeFormAddressElementType.Element8]: addressFieldConfig?.element8,
    [FreeFormAddressElementType.Element9]: addressFieldConfig?.element9,
    [FreeFormAddressElementType.Element10]: addressFieldConfig?.element10,
    [FreeFormAddressElementType.Element11]: addressFieldConfig?.element11,
    [FreeFormAddressElementType.Element12]: addressFieldConfig?.element12,
    [FreeFormAddressElementType.Element13]: addressFieldConfig?.element13,
    [FreeFormAddressElementType.Element14]: addressFieldConfig?.element14,
    [FreeFormAddressElementType.Element15]: addressFieldConfig?.element15,
    ...(paymentSourceKind === PaymentSourceKind.CreditCard
      ? {
          cardHolder:
            "payment" in formConfig
              ? formConfig.payment.creditCard?.cardHolder
              : undefined,
          cardNumber:
            "payment" in formConfig
              ? formConfig.payment.creditCard?.cardNumber
              : undefined,
          cardExpiry:
            "payment" in formConfig
              ? formConfig.payment.creditCard?.cardExpiry
              : undefined,
          cardVerificationCode:
            "payment" in formConfig
              ? formConfig.payment.creditCard?.cvc
              : undefined,
        }
      : {
          cardHolder: undefined,
          cardNumber: undefined,
          cardExpiry: undefined,
          cardVerificationCode: undefined,
        }),
    ...(paymentSourceKind === PaymentSourceKind.DirectDebit
      ? {
          directDebitAccountHolder:
            "payment" in formConfig
              ? formConfig.payment.directDebit?.accountHolder
              : undefined,
          directDebitAccountNumber:
            "payment" in formConfig &&
            formConfig.payment.directDebit?.type === "NonIBAN"
              ? formConfig.payment.directDebit?.accountNumber
              : undefined,
          directDebitSortCode:
            "payment" in formConfig &&
            formConfig.payment.directDebit?.type === "NonIBAN"
              ? formConfig.payment.directDebit?.sortCode
              : undefined,
          directDebitIBAN:
            "payment" in formConfig &&
            formConfig.payment.directDebit?.type === "IBAN"
              ? formConfig.payment.directDebit?.iban
              : undefined,
        }
      : {
          directDebitAccountHolder: undefined,
          directDebitAccountNumber: undefined,
          directDebitSortCode: undefined,
          directDebitIBAN: undefined,
        }),
  };
}

function validRegExp(
  validation: RegExpValidationDefinition,
  value: string
): boolean {
  return !!value.match(validation.expression);
}

function validCVC(
  value: string,
  validationContext: ValidationContext
): boolean {
  const expectedCvcLength = validationContext.cardNumber.value?.startsWith("37")
    ? 4
    : 3;
  return !!value.match(/\d+/) && value.length === expectedCvcLength;
}

function validMinimumAge(
  validation: MinimumAgeValidationDefinition,
  value: string,
  now?: number
): boolean {
  const dob = new Date(value);
  dob.setFullYear(dob.getFullYear() + validation.minimumAgeInYears);

  return dob <= new Date(now || Date.now());
}

export function validSoonExpiration(
  validation: SoonExpirationValidationDefinition,
  value: string,
  now?: number
): boolean {
  const expiryValues = value.split("-").map(Number);
  const expiresInMonths = expiryValues[0] * 12 + expiryValues[1];

  const nowDate = new Date(now || Date.now());
  const expirationLimit = nowDate.getFullYear() * 12 + nowDate.getMonth() + 1;

  return (
    expiresInMonths >= expirationLimit + validation.minimumExpirationInMonths
  );
}

function validDate(value: string): boolean {
  return Number.isFinite(new Date(value).getDate());
}

function ValidUKAccount(validationContext: ValidationContext): boolean {
  const accountNumber = validationContext.directDebitAccountNumber.value;
  const sortCode = validationContext.directDebitSortCode.value;
  if (accountNumber && sortCode) {
    return (
      isCheckable({ sortCode, accountNumber }) &&
      checkModulus({ sortCode, accountNumber })
    );
  } else {
    return false;
  }
}

// eslint-disable-next-line consistent-return
function valid(
  validation: ValidationDefinition,
  value: string,
  validationContext: ValidationContext
): boolean {
  switch (validation.kind) {
    case ValidationKind.Expression:
      return validRegExp(validation, value);
    case ValidationKind.Luhn:
      return validLuhn(value);
    case ValidationKind.CVC:
      return validCVC(value, validationContext);
    case ValidationKind.MinimumAge:
      return validMinimumAge(validation, value);
    case ValidationKind.SoonExpiration:
      return validSoonExpiration(validation, value);
    case ValidationKind.ValidDate:
      return validDate(value);
    case ValidationKind.IBAN:
      return validIBAN(value);
    case ValidationKind.ValidUKAccount:
      return ValidUKAccount(validationContext);
    default:
      ((x: never) => {
        throw new Error(`${x} was unhandled!`);
      })(validation);
  }
}

export function getFieldValidationErrors(
  config: ValidatedFormFieldConfig,
  value: string | undefined,
  validationContext: ValidationContext
): ReactNode[] {
  if (value === undefined) {
    const { requiredFieldErrorText } = config;
    if (requiredFieldErrorText) {
      return [requiredFieldErrorText];
    }
    return [];
  }

  if (config.validations === undefined) {
    return [];
  }

  return config.validations.reduce(
    (accu: ReactNode[], validation: ValidationDefinition): ReactNode[] => {
      if (!valid(validation, value, validationContext)) {
        accu.push(
          <Text
            args={argsForValidation(validation)}
            value={validation.errorMessage}
            regex={/{(.+?)}/g}
          />
        );
      }
      return accu;
    },
    [] as ReactNode[]
  );
}

export function createDetailsPageErrorsSelector(formConfig: FormConfig) {
  return function detailsPageErrorSelector(
    rootState: RootState
  ): { [key in keyof FormState]: boolean } {
    const validationContext = validationContextSelector(rootState);

    return {
      ...mapValues(
        getStringFieldValidators(
          formConfig,
          rootState.countryCode.value,
          rootState.paymentSourceKind
        ),
        (config, key) =>
          config
            ? getFieldValidationErrors(
                config,
                rootState[key].value,
                validationContext
              ).length > 0
            : false
      ),

      termsAndConditionsConsent: !!(
        formConfig.termsAndConditions && !rootState.termsAndConditionsConsent
      ),

      giftAidConsent: false,
      phoneContactConsent: false,
      smsContactConsent: false,
      emailContactConsent: false,
      postContactConsent: false,
      startDate: false,
      paymentDay: !!(
        "payment" in formConfig &&
        formConfig.payment.paymentDays &&
        !rootState.paymentDay
      ),
    };
  };
}

export function createIndexPageErrorsSelector(formConfig: FormConfig) {
  return function indexPageErrorSelector(
    rootState: RootState
  ): Partial<{ [key in keyof FormState]: boolean }> {
    const amount = amountSelector(rootState);
    if (
      "payment" in formConfig &&
      (amount.value <= 0 || rootState.paymentFrequency === undefined)
    ) {
      return { customAmountString: true };
    }
    return pick(rootState.errors, "customAmountString");
  };
}

export function errorsForEmailAddress(
  config: EmailAddressFieldConfig,
  emailAddress: string | undefined,
  appraisal: SuccessfulKickboxAppraisal | undefined,
  validationContext: ValidationContext
): ReactNode[] {
  const validationErrors = getFieldValidationErrors(
    config,
    emailAddress,
    validationContext
  );
  if (validationErrors.length > 0) {
    return validationErrors;
  }

  if (
    "validateAddress" in config &&
    appraisal &&
    (appraisal.disposable || appraisal.result === "undeliverable")
  ) {
    return [config.validateAddress.invalidErrorText];
  }

  return [];
}

export function errorsForIBAN(
  config: IBANFieldConfig,
  iban: string | undefined,
  appraisal: IBANValidationResult | undefined,
  validationContext: ValidationContext
): ReactNode[] {
  const validationErrors = getFieldValidationErrors(
    config,
    iban,
    validationContext
  );
  if (validationErrors.length > 0) {
    return validationErrors;
  }
  return [];
}

export function normalizeFieldValue(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed.length > 0) {
    return trimmed;
  }
  return undefined;
}
